import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  DashboardPayload,
  Dashboard,
  DashboardAPI,
  DuplicateDashboardPayload,
  Layout,
} from 'api/dashboard';
import { fiveMinutes } from './constants';

const DASHBOARDS_QUERY_KEY = ['dashboards'];
const useDashboardsQuery = (
  options: UseQueryOptions<
    Dashboard[],
    unknown,
    Dashboard[],
    typeof DASHBOARDS_QUERY_KEY
  > = {},
): UseQueryResult<Dashboard[]> => {
  return useQuery({
    staleTime: fiveMinutes,
    ...options,
    queryKey: DASHBOARDS_QUERY_KEY,
    queryFn: () => DashboardAPI.getAllDashboards(),
  });
};

const DASHBOARD_VIEWS_QUERY_KEY = 'DASHBOARD_VIEWS_QUERY';

export const getDashboardViewsQueryKey = (): [string] => [
  DASHBOARD_VIEWS_QUERY_KEY,
];

const getDashboardQueryKey = (dashboardId: number): [string, string] => [
  'dashboards',
  `${dashboardId}`,
];
const getErrorStatus = (error: unknown): number | undefined => {
  const response = (error as Record<string, unknown>)?.response;

  if (response && typeof response === 'object') {
    const { status } = response as Record<string, unknown>;

    return typeof status === 'number' ? status : undefined;
  }

  return undefined;
};

const useDashboardQuery = (dashboardId: number): UseQueryResult<Dashboard> => {
  return useQuery(
    getDashboardQueryKey(dashboardId),
    () => DashboardAPI.getDashboard(dashboardId),
    {
      enabled: !!dashboardId && dashboardId !== -1,
      retry: (failureCount, error) => {
        if (getErrorStatus(error) === 403) {
          return false;
        }

        return failureCount < 3;
      },
    },
  );
};

const getDashboardQueryKeyV2 = (dashboardId: number): [string, string] => [
  'dashboards-v2',
  `${dashboardId}`,
];

const useDashboardQueryV2 = (
  dashboardId: number,
): UseQueryResult<Dashboard> => {
  return useQuery(
    getDashboardQueryKeyV2(dashboardId),
    () => DashboardAPI.getDashboardV2(dashboardId),
    {
      enabled: !!dashboardId && dashboardId !== -1,
      retry: (failureCount, error) => {
        if (getErrorStatus(error) === 403) {
          return false;
        }

        return failureCount < 3;
      },
    },
  );
};

const useCreateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DashboardPayload
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => DashboardAPI.createDashboard(payload), {
    onSuccess: (newDashboard, variables) => {
      const merged: Dashboard = {
        ...newDashboard,
        restrictedIds: newDashboard.restrictedIds ?? variables.restrictedIds,
      };

      queryClient.setQueryData<Dashboard[]>(
        DASHBOARDS_QUERY_KEY,
        (old = []) => [...old, merged],
      );

      queryClient.setQueryData(
        getDashboardQueryKeyV2(merged.dashboardId),
        merged,
      );
    },
  });
};

const predicateById = (dashboardId) => (dashboard: Dashboard) =>
  dashboard.dashboardId === dashboardId;
const filterExceptId = (dashboardId) => (dashboard: Dashboard) =>
  dashboard.dashboardId !== dashboardId;

const updateDashboard =
  (newDashboard: Dashboard, restrictedIds?: number[]) =>
  (old: Dashboard[] = []) => {
    const idx = old.findIndex(predicateById(newDashboard.dashboardId));

    if (idx === -1) {
      return old;
    }

    return old.map((d) => {
      if (d.dashboardId !== newDashboard.dashboardId) {
        return d;
      }

      return {
        ...newDashboard,
        widgets: newDashboard.widgets?.length
          ? newDashboard.widgets
          : d.widgets,
        restrictedIds: newDashboard.restrictedIds ?? restrictedIds,
      };
    });
  };

const deleteDashboard =
  (idToBeDeleted: number) =>
  (old: Dashboard[] = []) => {
    return old.filter(filterExceptId(idToBeDeleted));
  };

interface DashboardPayloadWithId extends DashboardPayload {
  dashboardId: number;
}

interface UpdateLayoutPayload {
  dashboardId: number;
  layout: Layout;
}

const useUpdateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DashboardPayloadWithId
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ dashboardId, ...rest }) =>
      DashboardAPI.updateDashboard(dashboardId, rest),
    {
      onSuccess: (newDashboard: Dashboard, variables) => {
        const merged: Dashboard = {
          ...newDashboard,
          restrictedIds: newDashboard.restrictedIds ?? variables.restrictedIds,
        };

        queryClient.setQueryData<Dashboard[]>(
          DASHBOARDS_QUERY_KEY,
          updateDashboard(merged),
        );

        queryClient.setQueryData(
          getDashboardQueryKeyV2(merged.dashboardId),
          (oldDashboard: Dashboard | undefined) => {
            if (!oldDashboard) {
              return merged;
            }

            return {
              ...merged,
              widgets: merged.widgets?.length
                ? merged.widgets
                : oldDashboard.widgets,
            };
          },
        );
      },
    },
  );
};

const useUpdateDashboardLayoutMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  UpdateLayoutPayload
> => {
  return useMutation(
    ({ dashboardId, layout }) =>
      DashboardAPI.updateDashboardLayout(dashboardId, layout),
    {},
  );
};

const useDuplicateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DuplicateDashboardPayload
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => DashboardAPI.duplicateDashboard(payload), {
    onSuccess: (newDashboard: Dashboard, variables) => {
      const merged: Dashboard = {
        ...newDashboard,
        restrictedIds: newDashboard.restrictedIds ?? variables.restrictedIds,
      };

      queryClient.setQueryData<Dashboard[]>(
        DASHBOARDS_QUERY_KEY,
        (old = []) => [...old, merged],
      );

      queryClient.setQueryData(
        getDashboardQueryKeyV2(merged.dashboardId),
        merged,
      );
    },
  });
};

const useDeleteDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (dashboardId: number) => DashboardAPI.deleteDashboard(dashboardId),
    {
      onSuccess: (_, dashboardId) => {
        queryClient.setQueryData<Dashboard[]>(
          DASHBOARDS_QUERY_KEY,
          deleteDashboard(dashboardId),
        );
      },
    },
  );
};

const useInitDashboardMutation = (): UseMutationResult<{
  dashboard: Dashboard[];
}> => {
  return useMutation(() => DashboardAPI.init());
};

export {
  DASHBOARDS_QUERY_KEY,
  useDashboardsQuery,
  getDashboardQueryKey,
  useDashboardQuery,
  useCreateDashboardMutation,
  useInitDashboardMutation,
  useDeleteDashboardMutation,
  useUpdateDashboardMutation,
  useDuplicateDashboardMutation,
  useUpdateDashboardLayoutMutation,
  useDashboardQueryV2,
  getDashboardQueryKeyV2,
  getErrorStatus,
};
